import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Shield, User as UserIcon, Trash2, Pencil, X, Check } from "lucide-react";

const PERM_LABELS = {
  view_dashboard: "View Dashboard",
  manage_transactions: "Manage Transactions",
  import_statements: "Import Statements",
  view_reports: "View Reports",
  manage_accounts: "Manage Accounts",
  manage_company: "Manage Company",
};

const emptyUser = { email: "", name: "", password: "", role: "user", permissions: ["view_dashboard"], company_ids: [] };

export default function Users() {
  const { companies } = useApp();
  const [users, setUsers] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Failed to load users"));
  useEffect(() => {
    load();
    api.get("/permissions").then(({ data }) => setAllPerms(data.permissions)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      if (modal.id) {
        await api.put(`/users/${modal.id}`, {
          name: modal.name, role: modal.role, permissions: modal.permissions,
          company_ids: modal.company_ids, ...(modal.password ? { password: modal.password } : {}),
        });
      } else {
        await api.post("/users", modal);
      }
      toast.success("User saved"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePerm = (p) => {
    const has = modal.permissions.includes(p);
    setModal({ ...modal, permissions: has ? modal.permissions.filter((x) => x !== p) : [...modal.permissions, p] });
  };
  const toggleCompany = (id) => {
    const has = modal.company_ids.includes(id);
    setModal({ ...modal, company_ids: has ? modal.company_ids.filter((x) => x !== id) : [...modal.company_ids, id] });
  };

  const editUser = (u) => {
    const cids = companies.filter((c) => (c.member_ids || []).includes(u.id)).map((c) => c.id);
    setModal({ ...u, password: "", company_ids: cids });
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Users & Permissions</h1>
          <p className="text-slate-500 text-sm mt-0.5">{users.length} users · super admin only</p>
        </div>
        <button data-testid="add-user-btn" onClick={() => setModal({ ...emptyUser })}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 active:scale-95">
          <Plus size={18} /> Add User
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-5 py-3 font-semibold">User</th>
                <th className="text-left px-5 py-3 font-semibold">Role</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Permissions</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold">{(u.name || "U").slice(0, 1)}</div>
                      <div><p className="font-semibold text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 w-fit ${u.role === "super_admin" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
                      {u.role === "super_admin" ? <Shield size={12} /> : <UserIcon size={12} />}{u.role === "super_admin" ? "Super Admin" : "User"}
                    </span>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {u.role === "super_admin" ? <span className="text-xs text-slate-400">All permissions</span> :
                        (u.permissions || []).map((p) => <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500">{PERM_LABELS[p] || p}</span>)}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => editUser(u)} className="p-1.5 text-slate-400 hover:text-indigo-600"><Pencil size={15} /></button>
                      <button data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
              <h3 className="font-heading font-bold text-lg">{modal.id ? "Edit User" : "Add User"}</h3>
              <button onClick={() => setModal(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Full Name"><input data-testid="user-name" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} className={inp} /></Fld>
                <Fld label="Email"><input data-testid="user-email" value={modal.email} disabled={!!modal.id} onChange={(e) => setModal({ ...modal, email: e.target.value })} className={inp} /></Fld>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label={modal.id ? "New Password (optional)" : "Password"}><input data-testid="user-password" type="password" value={modal.password} onChange={(e) => setModal({ ...modal, password: e.target.value })} className={inp} /></Fld>
                <Fld label="Role">
                  <select data-testid="user-role" value={modal.role} onChange={(e) => setModal({ ...modal, role: e.target.value })} className={inp}>
                    <option value="user">User</option><option value="super_admin">Super Admin</option>
                  </select>
                </Fld>
              </div>
              {modal.role === "user" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">Permissions</label>
                    <div className="grid grid-cols-2 gap-2">
                      {allPerms.map((p) => (
                        <button key={p} data-testid={`perm-${p}`} onClick={() => togglePerm(p)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm text-left ${modal.permissions.includes(p) ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                          <span className={`w-4 h-4 rounded grid place-items-center ${modal.permissions.includes(p) ? "bg-indigo-600" : "border border-slate-300"}`}>
                            {modal.permissions.includes(p) && <Check size={11} className="text-white" />}
                          </span>
                          {PERM_LABELS[p] || p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">Company Access</label>
                    <div className="flex flex-wrap gap-2">
                      {companies.map((c) => (
                        <button key={c.id} onClick={() => toggleCompany(c.id)}
                          className={`px-3 py-1.5 rounded-xl border text-sm font-medium ${modal.company_ids.includes(c.id) ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-5 border-t border-slate-100 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
              <button data-testid="user-save-btn" onClick={save} className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none disabled:bg-slate-50";
const Fld = ({ label, children }) => (<div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>);
